import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography, Button, Switch, FormControlLabel, ToggleButton, ToggleButtonGroup, Paper, Autocomplete, Dialog, DialogContent, DialogActions, IconButton, TextField, InputAdornment, FormControl, Slider, Modal } from '@mui/material';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import SearchIcon from '@mui/icons-material/Search';
import ctd from 'country-telephone-data';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { pageContainerSx, cardSx, fieldSx, twoColRowSx, buttonRowSx, breadcrumbRowSx, breadcrumbLabelSx, pageTitleSx, labelSx, inputBoxSx, inputTwoColSx, mapCanvasSx, saveButtonSx, cancelButtonSx, jurisdictionCardSx, jurisdictionTitleSx, drawControlsSx, drawButtonSx, selectDistrictSx, clearButtonSx, mapHelperRowSx, clickTextSx, stationDetailsCardSx, stationDetailsTitleSx, stationFieldContainerSx, stationInputSx, twoColFieldSx, inputHalfSx, toggleButtonBaseSx, slidingIndicatorSx, drawHelperTitleSx, drawHelperActionSx, textSx, dropdownSx } from './createStation.styles';
import { searchBoxSx, searchFieldSx, mapPlaceholderSx, radiusContainerSx, sliderWrapperSx, radiusValueSx, repositionWrapperSx, coordsBadgeSx, deactivateButtonSx } from '../node/createNode.styles';
import { sectionTitleSx, sectionSublineSx } from '../../styles/sectionTokens';
import ChevronRight from '../../components/ChevronRight';
import { headerRowSx, identitySectionSx, leftTextSx, pageBoxSx, personalSectionSx, sectionPaperSx, nodeDetailsSx, formInputSx } from './LEStations.styles';
import userIcon from '../../assets/user-icon.svg';
import CloseIcon from '@mui/icons-material/Close';
import { nodeCitySx, activeTagSx, inactiveTagSx, activeDotColor, inactiveDotColor, powerIconSx } from '../node/operatingNodes.styles';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Map, { Marker, Source, Layer, type MapRef } from 'react-map-gl/mapbox';
import { getDistricts } from '../../services/nodeService';
import { createLEStation, editLEStation, setLEStationActive, getLEStationUsers, createLEStationUser, setLEStationUserActive } from '../../services/leStationService';
import { modalContainerSx, modalHeaderSx, modalTitleSx, modalCloseIconSx, modalContentSx, modalWarningRowSx, modalCancelButtonSx, modalDeactivateButtonSx, modalBodyTextSx } from '../node/createNode.styles';

const MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v11';

// users are fetched from API when the view toggles to 'userslist'

const CreateStation: React.FC<{ onCancel?: () => void; onSave?: (data: any) => void; initialData?: any }> = ({ onCancel, onSave, initialData }) => {
    const [stationName, setStationName] = useState('');
    const [primaryName, setPrimaryName] = useState('');
    const [primaryEmail, setPrimaryEmail] = useState('');
    const [district, setDistrict] = useState('');

    const [drawing, setDrawing] = useState(false);
    const [jurisdictionMode, setJurisdictionMode] = useState<'draw' | 'select'>('draw');
    // fix typo in view mode union so toggle values match
    const [viewMode, setViewMode] = useState<'editDetails' | 'userslist'>('editDetails');

    // KAN-133: pin the sliding indicator to the active button (left edge for the
    // first option, right edge for the second) instead of a hardcoded pixel offset.
    // The toggle group is width:100% with space-between and MUI adds inter-button
    // borders, so the old fixed 121.5234375px never matched the second button and
    // the toggle bar looked mis-aligned. left/right pinning is width-independent.
    const indicatorPos = jurisdictionMode === 'draw'
        ? { left: '4px', right: 'auto' }
        : { left: 'auto', right: '4px' };
    const viewIndicatorLeft = viewMode === 'editDetails' ? '4px' : '121.5234375px';
    // districts will be loaded from the server when the user switches to "select" mode
    const [districts, setDistricts] = useState<string[]>([]);
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    const fetchDistricts = async () => {
        setLoadingDistricts(true);
        try {
            const list = await getDistricts();
            setDistricts(list);
        } catch (err: any) {
            // getDistricts already dispatches notification via service notify helper
        } finally {
            setLoadingDistricts(false);
        }
    };

    const jurisdictionPaperSx = { ...(sectionPaperSx as any), ...(identitySectionSx as any) } as any;
    // store center coords and radius for Mapbox usage (single-point + radius, like CreateNode)
    const [points, setPoints] = useState<Array<{ lat: number; lng: number }>>([]); // kept for backward-compat but unused
    const [polygonGeoJSON, setPolygonGeoJSON] = useState<any | null>(null);
    const [radius, setRadius] = useState<number>((initialData?.radius ?? 150) / 1000);
    const [radiusTouched, setRadiusTouched] = useState<boolean>(false);
    // single coords for select mode (latitude/longitude like CreateNode)
    const [coords, setCoords] = useState<{ lat: number; lng: number }>({ lat: initialData?.latitude ?? 51.51, lng: initialData?.longitude ?? -0.134 });
    const [mapAuthError, setMapAuthError] = useState<string | null>(null);
    const [mapChecking, setMapChecking] = useState<boolean>(false);
    const [coordsTouched, setCoordsTouched] = useState<boolean>(false);
    const [districtTouched, setDistrictTouched] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Array<any>>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef<HTMLDivElement | null>(null);
    const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN ?? '';
    const isValidMapboxToken = Boolean(mapboxToken && mapboxToken.startsWith('pk.'));
    const mapRef = useRef<MapRef | null>(null);
    const [users, setUsers] = useState<Array<{ id?: number; name: string; email: string; status: string }>>([]);
    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [pageSize, setPageSize] = useState<number>(10);
    const [stationUserRows, setStationUserRows] = useState<Array<any>>([]);
    const [stationUserRowCount, setStationUserRowCount] = useState<number>(0);
    const [stationUserLoading, setStationUserLoading] = useState<boolean>(false);
    // Add User dialog state
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newCountry, setNewCountry] = useState('+91');
    const [newMobile, setNewMobile] = useState('');
    const [userNotification, setUserNotification] = useState<{ show: boolean; message?: string; type?: 'success' | 'error' }>({ show: false });
    const notifTimerRef = useRef<number | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [addUserApiError, setAddUserApiError] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [userConfirmOpen, setUserConfirmOpen] = useState(false);
    const [userConfirmTarget, setUserConfirmTarget] = useState<any>(null);

    useEffect(() => {
    if (initialData) {
            setStationName(initialData.stationName || '');
            // support different shapes: some lists use `primaryContact`/`email` while form expects `primaryName`/`primaryEmail`
            setPrimaryName(initialData.primaryName ?? initialData.primaryContact ?? '');
            setPrimaryEmail(initialData.primaryEmail ?? initialData.email ?? '');
            setDistrict(initialData.district ?? initialData.jurisdiction ?? '');
            // prepopulate map centre and radius when editing
            if (initialData.latitude != null && initialData.longitude != null) {
                setCoords({ lat: Number(initialData.latitude), lng: Number(initialData.longitude) });
                // ensure the Draw map is shown when editing an existing station that has coords
                setJurisdictionMode('draw');
                setDrawing(true);
            }
            if (initialData.radius != null) setRadius(Number(initialData.radius) / 1000);
            // users will be loaded from API when toggled to Users view
        }
    }, [initialData]);

    // when the edit form opens, fetch the users count for this station so the Users toggle shows the correct number
    useEffect(() => {
        let cancelled = false;
        const loadCount = async () => {
            if (!initialData || !initialData.id) return;
            try {
                // fetch only first page with page_size=1 to get meta.total
                const resp = await getLEStationUsers(initialData.id, 1, 10);
                if (cancelled) return;
                const total = Number(resp?.meta?.total ?? resp?.meta?.total_count ?? 0) || 0;
                setStationUserRowCount(total);
            } catch (err) {
                // silently ignore; keep count as 0
                console.error('Failed to fetch station users count', err);
            }
        };
        loadCount();
        return () => { cancelled = true; };
    }, [initialData?.id]);

    // fetch users for this station when in userslist view or when pagination changes
    useEffect(() => {
        if (viewMode !== 'userslist') return;
        let mounted = true;
        const loadUsers = async () => {
            if (!initialData || !initialData.id) return;
            setStationUserLoading(true);
            try {
                const apiPage = (paginationModel.page ?? 0) + 1;
                const apiPageSize = paginationModel.pageSize ?? 5;
                const resp = await getLEStationUsers(initialData.id, apiPage, apiPageSize);
                if (!mounted) return;
                const mapped = (resp.users || []).map((u: any) => ({
                    id: u.user_id ?? u.id ?? u.email ?? `${u.name ?? 'user'}-${Math.random()}`,
                    user_id: u.user_id ?? u.id,
                    name: u.name ?? u.full_name ?? u.email ?? 'User',
                    email: u.email ?? '',
                    phone: u.phone ?? u.phone_number ?? '',
                    status: u.active ? 'Active' : 'Inactive'
                }));
                setStationUserRows(mapped);
                setStationUserRowCount(resp.meta?.total ?? mapped.length);
            } catch (err) {
                console.error('Failed to load station users', err);
                setStationUserRows([]);
                setStationUserRowCount(0);
            } finally {
                if (mounted) setStationUserLoading(false);
            }
        };
        loadUsers();
        return () => { mounted = false; };
    }, [viewMode, initialData?.id, paginationModel.page, paginationModel.pageSize]);

    // If editing and a district value exists on the initial data, fetch districts so the dropdown can pre-select
    useEffect(() => {
        if (!initialData) return;
        const want = initialData.district ?? initialData.jurisdiction ?? '';
        if (want && !districts.length) {
            fetchDistricts();
        }
    }, [initialData]);

    // When districts load, if initialData has a district that matches one in the list, select it (case-insensitive)
    useEffect(() => {
        if (!initialData) return;
        if (!districts || !districts.length) return;
        const want = (initialData.district ?? initialData.jurisdiction ?? '').toString();
        if (!want) return;
        const found = districts.find((d) => d && String(d).toLowerCase() === want.toLowerCase());
        if (found) setDistrict(found);
    }, [districts, initialData]);

    // Preflight check for Mapbox style access (detect invalid token)
    useEffect(() => {
        if (!isValidMapboxToken) {
            setMapAuthError(null);
            return;
        }
        let cancelled = false;
        const check = async () => {
            setMapChecking(true);
            try {
                const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
                const url = `${host}/styles/v1/mapbox/streets-v11?access_token=${mapboxToken}`;
                const res = await fetch(url, { method: 'GET' });
                if (cancelled) return;
                if (!res.ok) {
                    if (res.status === 401 || res.status === 403) {
                        setMapAuthError('Mapbox token is invalid or unauthorized (HTTP ' + res.status + '). Provide a valid public token starting with "pk." in your .env file.');
                    } else {
                        setMapAuthError('Failed to load Mapbox style: HTTP ' + res.status);
                    }
                } else {
                    setMapAuthError(null);
                }
            } catch (err) {
                if (cancelled) return;
                setMapAuthError('Network error while checking Mapbox token (see console)');
            } finally {
                if (!cancelled) setMapChecking(false);
            }
        };
        check();
        return () => { cancelled = true; };
    }, [mapboxToken, isValidMapboxToken]);

    // Auto-detect current location for new stations
    useEffect(() => {
        if (initialData) return; // editing: use existing coords
        const stored = localStorage.getItem('wualt:lastLocation');
        if (stored) {
            try {
                const { lat, lng } = JSON.parse(stored);
                if (typeof lat === 'number' && typeof lng === 'number') {
                    setCoords({ lat, lng });
                    return;
                }
            } catch {}
        }
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    setCoords({ lat, lng });
                    localStorage.setItem('wualt:lastLocation', JSON.stringify({ lat, lng }));
                },
                () => {}
            );
        }
    }, []);

    // Debounced fetching of geocoding suggestions
    useEffect(() => {
        if (!isValidMapboxToken) return;
        if (!searchQuery || searchQuery.trim().length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
        const t = setTimeout(async () => {
            try {
                const q = encodeURIComponent(searchQuery.trim());
                const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
                const url = `${host}/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxToken}&autocomplete=true&limit=5`;
                const res = await fetch(url);
                if (!res.ok) { setSuggestions([]); setShowSuggestions(true); return; }
                const body = await res.json();
                const feats = Array.isArray(body?.features) ? body.features : [];
                setSuggestions(feats);
                setShowSuggestions(true);
            } catch (err) {
                setSuggestions([]);
                setShowSuggestions(true);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [searchQuery, mapboxToken, isValidMapboxToken]);

    // hide suggestions when clicking outside
    useEffect(() => {
        const onDocClick = (ev: MouseEvent) => {
            if (!searchBoxRef.current) return;
            if (!(ev.target instanceof Node)) return;
            if (!searchBoxRef.current.contains(ev.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    const closeConfirm = () => { setConfirmOpen(false); };

    const confirmToggleUser = async () => {
        if (!userConfirmTarget || !initialData?.id) return;
        const userId = userConfirmTarget.user_id ?? userConfirmTarget.id;
        // KAN-129: derive action from current status — same flow activates or deactivates.
        const nextActive = userConfirmTarget.status === 'Inactive';
        try {
            const resp = await setLEStationUserActive(initialData.id, userId, nextActive);
            if (resp?.success === false) {
                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: resp?.message ?? (nextActive ? 'Failed to activate user' : 'Failed to deactivate user') } }));
            } else {
                setStationUserRows((prev) => prev.map((u) => (u.id === userConfirmTarget.id ? { ...u, status: nextActive ? 'Active' : 'Inactive' } : u)));
                window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: nextActive ? 'User activated' : 'User deactivated' } }));
            }
        } catch (err: any) {
            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? (nextActive ? 'Failed to activate user' : 'Failed to deactivate user') } }));
        }
        setUserConfirmOpen(false);
        setUserConfirmTarget(null);
    };
    const confirmDeactivate = async () => {
        // attempt to deactivate via API when editing an existing station
        try {
            const id = initialData?.id;
            if (!id) {
                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: 'Cannot determine station id for deactivate' } }));
                closeConfirm();
                return;
            }
            const resp = await setLEStationActive(id, false);
            if (resp?.success === false) {
                setFormError(resp?.message ?? 'Failed to deactivate station');
            } else {
                window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'Station deactivated' } }));
                onSave && onSave(resp);
            }
        } catch (err: any) {
            console.error('[CreateStation] Deactivate failed', err);
            setFormError(err?.message ?? 'Failed to deactivate station');
        } finally {
            closeConfirm();
        }
    };

    // cleanup notification timer on unmount
    useEffect(() => {
        return () => {
            if (notifTimerRef.current) {
                window.clearTimeout(notifTimerRef.current);
                notifTimerRef.current = null;
            }
        };
    }, []);

    const onMapClick = async (e: any) => {
    // react-map-gl map click event returns lngLat as [lng, lat]
        try {
            let lng: number | undefined;
            let lat: number | undefined;

            // common shapes: array [lng, lat]
            if (Array.isArray(e?.lngLat)) {
                [lng, lat] = e.lngLat;
            } else if (e?.lngLat && typeof e.lngLat.toArray === 'function') {
                [lng, lat] = e.lngLat.toArray();
            } else if (e?.lngLat && typeof e.lngLat === 'object' && 'lng' in e.lngLat && 'lat' in e.lngLat) {
                lng = (e.lngLat as any).lng;
                lat = (e.lngLat as any).lat;
            } else if (Array.isArray(e?.lngLat?.lngLat)) {
                [lng, lat] = e.lngLat.lngLat;
            } else if (typeof e?.lng === 'number' && typeof e?.lat === 'number') {
                lng = e.lng;
                lat = e.lat;
            }

            if (lng == null || lat == null) return;
            const lngNum = lng as number;
            const latNum = lat as number;
            // always set center coords (match CreateNode behaviour)
            setCoords({ lat: latNum, lng: lngNum });
            setCoordsTouched(true);
            try {
                const map = mapRef.current?.getMap && mapRef.current.getMap();
                if (map && typeof map.flyTo === 'function') map.flyTo({ center: [lngNum, latNum], zoom: 12 });
            } catch (err) { /* ignore */ }
        } catch (err) {
            // ignore
        }
    };

    // create GeoJSON polygon approximating a circle around coords
    const createCircleGeoJSON = (center: [number, number], radiusMeters: number, pointsCount = 64) => {
        const coordsArr: [number, number][] = [];
        const R = 6378137; // Earth radius in meters (WGS84)
        const [lng, lat] = center;
        for (let i = 0; i < pointsCount; i++) {
            const angle = (i * 2 * Math.PI) / pointsCount;
            const dx = radiusMeters * Math.cos(angle);
            const dy = radiusMeters * Math.sin(angle);
            const dLat = dy / R;
            const dLng = dx / (R * Math.cos((lat * Math.PI) / 180));
            const pointLat = lat + (dLat * 180) / Math.PI;
            const pointLng = lng + (dLng * 180) / Math.PI;
            coordsArr.push([pointLng, pointLat]);
        }
        coordsArr.push(coordsArr[0]);
        return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coordsArr] }, properties: {} } as any;
    };

    // update polygonGeoJSON whenever coords or radius change (select mode)
    useEffect(() => {
        if (!coords || coords.lat == null || coords.lng == null) {
            setPolygonGeoJSON(null);
            return;
        }
        const feat = createCircleGeoJSON([coords.lng, coords.lat], radius * 1000, 64);
        setPolygonGeoJSON(feat);
        // clear legacy points
        setPoints([]);
    }, [coords.lat, coords.lng, radius]);

    // When coords change (including from geolocation), center the map viewport
    useEffect(() => {
        const t = setTimeout(() => {
            try {
                const map = mapRef.current?.getMap && mapRef.current.getMap();
                if (map && typeof map.flyTo === 'function' && coords && typeof coords.lng === 'number' && typeof coords.lat === 'number') {
                    map.flyTo({ center: [coords.lng, coords.lat], zoom: 12 });
                }
            } catch (err) { /* ignore */ }
        }, 300);
        return () => clearTimeout(t);
    }, [coords.lat, coords.lng]);

    // memoized circle feature used by Draw mode map to render when radius changes
    const circleFeature = useMemo(() => {
        if (!coords || coords.lat == null || coords.lng == null) return null;
        return createCircleGeoJSON([coords.lng, coords.lat], radius * 1000, 64);
    }, [coords.lng, coords.lat, radius]);

    const clearPoints = () => setPoints([]);
    // clear points and polygon
    const clearAllPoints = () => {
        setPoints([]);
        setPolygonGeoJSON(null);
    };

    // build polygon GeoJSON from clicked points
    useEffect(() => {
        if (!points || points.length < 2) {
            setPolygonGeoJSON(null);
            return;
        }

        if (points.length === 2) {
            const [p0, p1] = points;
            const coords = [
                [p0.lng, p0.lat],
                [p0.lng, p1.lat],
                [p1.lng, p1.lat],
                [p1.lng, p0.lat],
                [p0.lng, p0.lat],
            ];
            setPolygonGeoJSON({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } });
            return;
        }

        // 3+ points: create polygon by closing the ring
        const coords = points.map((p) => [p.lng, p.lat]);
        if (coords.length > 0) {
            const first = coords[0];
            const last = coords[coords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first);
        }
        setPolygonGeoJSON({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } });
    }, [points]);

    const openAddUserDialog = () => {
        setNewName('');
        setNewEmail('');
        setNewCountry('+91');
        setNewMobile('');
        setErrors({});
        setAddUserApiError(null);
        setOpenAddDialog(true);
    };

    const closeAddUserDialog = () => {
        setOpenAddDialog(false);
    };

    const createUser = async () => {
        const errors: Record<string, string> = {};
        if (!newName || !newName.trim()) errors.name = 'Name is required';
        if (!newEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail)) errors.email = 'Valid email is required';
        setErrors(errors);
        if (Object.keys(errors).length) return;
        if (!initialData || !initialData.id) {
            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: 'Cannot determine station id to add user' } }));
            return;
        }
        try {
                // handle phone: if number provided, country code is required; if no number, omit phone
                const country = (newCountry || '').trim();
                const number = (newMobile || '').trim();
                if (number && !country) {
                    setErrors({ ...errors, phone: 'Country code is required when providing a phone number' });
                    return;
                }
                const phoneValue = number ? `${country} ${number}`.trim() : undefined;
                const payload: any = { name: newName.trim(), email: newEmail.trim(), userRole: 'LEUser' };
                if (phoneValue) payload.phone = phoneValue;
            const resp = await createLEStationUser(initialData.id, payload);
            if (resp?.success === false) {
                setAddUserApiError(resp?.message ?? 'Failed to create user');
                return;
            }
            window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'User created' } }));
            // show local success notification
            setUserNotification({ show: true, message: resp?.message ?? 'User created', type: 'success' });
            if (notifTimerRef.current) window.clearTimeout(notifTimerRef.current);
            notifTimerRef.current = window.setTimeout(() => { setUserNotification({ show: false }); notifTimerRef.current = null; }, 3000);
            setOpenAddDialog(false);
            // reset to first page with default pageSize 10 and trigger reload (effect watches paginationModel)
            setPaginationModel({ page: 0, pageSize: 10 });
            // immediately refresh first page and count so DataGrid updates without a full page refresh
            try {
                setStationUserLoading(true);
                const pageSizeToUse = paginationModel.pageSize ?? 10;
                const listResp = await getLEStationUsers(initialData.id, 1, pageSizeToUse);
                const mapped = (listResp.users || []).map((u: any) => ({
                    id: u.user_id ?? u.id ?? u.email ?? `${u.name ?? 'user'}-${Math.random()}`,
                    user_id: u.user_id ?? u.id,
                    name: u.name ?? u.full_name ?? u.email ?? 'User',
                    email: u.email ?? '',
                    phone: u.phone ?? u.phone_number ?? '',
                    status: u.active ? 'Active' : 'Inactive'
                }));
                setStationUserRows(mapped);
                setStationUserRowCount(Number(listResp?.meta?.total ?? mapped.length) || 0);
            } catch (err) {
                // ignore list refresh errors
            } finally {
                setStationUserLoading(false);
            }
        } catch (err: any) {
            setAddUserApiError(err?.message ?? 'Failed to add user');
        }
    };

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150, sortable: false, renderCell: (params) => <Typography sx={nodeCitySx as any}>{params.row.name}</Typography> },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 180, sortable: false, renderCell: (params) => <Typography sx={nodeCitySx as any}>{params.row.email}</Typography> },
        {
            field: 'status', headerName: 'Status', width: 120, sortable: false, renderCell: (params) => (
                params.value === 'Active' ? (
                    <Box component="span" sx={activeTagSx as any}>
                        <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '16777200px', bgcolor: activeDotColor, top: '9.25px', left: '10px', opacity: 1 }} />
                        <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
                    </Box>
                ) : (
                    <Box component="span" sx={inactiveTagSx as any}>
                        <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '16777200px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px', opacity: 1 }} />
                        <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
                    </Box>
                )
            )
        },
        {
            field: 'actions', headerName: '', width: 60, sortable: false, renderCell: (params) => {
                const isInactive = params.row?.status === 'Inactive';
                const iconColor = isInactive ? '#9CA3AF' : '#6B7280';
                return (
                    // KAN-129: power toggle stays enabled for inactive rows to allow reactivation.
                    <IconButton
                        size="small"
                        title={isInactive ? 'Activate' : 'Deactivate'}
                        onClick={() => { setUserConfirmTarget(params.row); setUserConfirmOpen(true); }}
                    >
                        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PowerSettingsNewIcon sx={{ color: iconColor, ...powerIconSx as any }} />
                            {isInactive && (
                                <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: iconColor, transform: 'rotate(45deg)' }} />
                            )}
                        </Box>
                    </IconButton>
                );
            }
        },
    ];

    const userRows = users.map((u, idx) => ({ id: u.id ?? idx + 1, name: u.name, email: u.email, status: u.status }));

    return (
        <Box sx={pageBoxSx}>
            <Box mb={2}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button size="small" onClick={() => onCancel && onCancel()} sx={{ textTransform: 'none', p: 0, minWidth: 0, color: '#6B7280', fontSize: '13px' }}>LE Stations</Button>
                            <Typography sx={{ color: '#6B7280' }}><ChevronRight /></Typography>
                            <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>{initialData?.stationName ?? 'Create Station'}</Typography>
                        </Box>
                        {formError && (
                            <Box sx={{ position: 'absolute', right: 0, top: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, zIndex: 20 }}>
                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, flexShrink: 0 }}>
                                    <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
                                </Box>
                                <Typography sx={{ flex: 1, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151' }}>{formError}</Typography>
                                <Box component="span" onClick={() => setFormError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, ml: 1 }}>×</Box>
                            </Box>
                        )}
                        {!formError && userNotification.show && (
                            <Box sx={{ position: 'absolute', right: 0, top: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: userNotification.type === 'error' ? '#FEF2F2' : '#ECFDF3', border: userNotification.type === 'error' ? '1px solid #FCA5A5' : '1px solid #A9DEBF', display: 'flex', alignItems: 'center', px: 2, zIndex: 20 }}>
                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: userNotification.type === 'error' ? '#DE606C' : '#008A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    {userNotification.type === 'error' ? (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 9v4" stroke="#FFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 17h.01" stroke="#FFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    ) : (
                                        <svg width="13" height="13" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3L3 5L7 1" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    )}
                                </Box>
                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px' }}>{userNotification.message}</Typography>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ ...(headerRowSx as any), mb: 2 }}>
                        <Box sx={leftTextSx}>
                            <Typography sx={pageTitleSx}>{initialData?.stationName ?? 'Create Station'}</Typography>
                        </Box>
                    </Box>
                </Box>

                {initialData && (
                    <Box sx={{ ...(drawControlsSx as any), mb: 2 }}>
                        <Box sx={{ ...(slidingIndicatorSx as any), left: viewIndicatorLeft }} />
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(_e, newMode) => {
                                if (newMode === null) return;
                                const mode = newMode as 'editDetails' | 'userslist';
                                setViewMode(mode);
                                setDrawing(mode === 'editDetails');
                            }}
                            aria-label="edit-content-mode"
                            sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                        >
                            <ToggleButton value="editDetails" sx={[toggleButtonBaseSx as any, drawButtonSx as any]} aria-label="editDetails">Details</ToggleButton>
                            <ToggleButton value="userslist" sx={[toggleButtonBaseSx as any, selectDistrictSx as any]} aria-label="userslist">Users ({stationUserRowCount ?? 0})</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {(!initialData || viewMode === 'editDetails') && (
                    <>
                        <Paper sx={{ ...(sectionPaperSx as any), ...(personalSectionSx as any), mb: 1 }}>
                            <Typography sx={{ ...(sectionTitleSx as any), mb: 1 }}>Station Details</Typography>
                            <Box sx={stationFieldContainerSx as any}>
                                <Box>
                                    <Typography sx={labelSx as any}>Station name *</Typography>
                                    <Box sx={stationInputSx as any}>
                                        <input placeholder="e.g. Camden Police Station" value={stationName} onChange={(e) => setStationName(e.target.value)} style={{ border: 'none', outline: 'none', width: '100%' }} />
                                    </Box>
                                    {errors.name && <Typography color="error" variant="caption">{errors.name}</Typography>}
                                </Box>

                                <Box sx={twoColFieldSx as any}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <Typography sx={labelSx as any}>Primary contact name *</Typography>
                                            <Box sx={inputHalfSx as any}><input placeholder="DI Jane Smith" value={primaryName} onChange={(e) => setPrimaryName(e.target.value)} style={{ border: 'none', outline: 'none', width: '100%' }} /></Box>
                                            {errors.primaryName && <Typography color="error" variant="caption">{errors.primaryName}</Typography>}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <Typography sx={labelSx as any}>Primary contact email *</Typography>
                                            <Box sx={inputHalfSx as any}><input placeholder="di.smith@police.uk" value={primaryEmail} onChange={(e) => setPrimaryEmail(e.target.value)} style={{ border: 'none', outline: 'none', width: '100%' }} /></Box>
                                            {errors.primaryEmail && <Typography color="error" variant="caption">{errors.primaryEmail}</Typography>}
                                        </Box>
                                </Box>
                            </Box>
                        </Paper>

                        {/* reduce paper height when in 'select' mode (no map) */}
                        <Paper sx={{ ...jurisdictionPaperSx, mb: 3 }}>
                            <Typography sx={{ ...(sectionTitleSx as any), mb: 1 }}>Jurisdiction</Typography>
                            <Typography sx={{ ...(sectionSublineSx as any) }}>Define the coverage area for this station.</Typography>

                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ ...(drawControlsSx as any), mb: 2 }}>
                                    <Box sx={{ ...(slidingIndicatorSx as any), ...indicatorPos }} />
                                    <ToggleButtonGroup
                                        value={jurisdictionMode}
                                        exclusive
                                        onChange={(_e, newMode) => {
                                            if (newMode === null) return;
                                            setJurisdictionMode(newMode);
                                            setDrawing(newMode === 'draw');
                                            if (newMode === 'select') fetchDistricts();
                                        }}
                                        aria-label="jurisdiction-mode"
                                        sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                                    >
                                        <ToggleButton value="draw" sx={[toggleButtonBaseSx as any, drawButtonSx as any]} aria-label="draw">Select on map</ToggleButton>
                                        <ToggleButton value="select" sx={[toggleButtonBaseSx as any, selectDistrictSx as any]} aria-label="select">Select district</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                {jurisdictionMode === 'draw' && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={drawHelperTitleSx as any}>Draw the station's coverage area by clicking points on the map.</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ mt: 2, position: 'relative' }} ref={searchBoxRef}>
                                                <Box sx={{ ...searchBoxSx, position: 'relative' }}>
                                                    <TextField
                                                        size="small"
                                                        placeholder="Search address on map..."
                                                        fullWidth
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>), }}
                                                        sx={searchFieldSx}
                                                        onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                                                    />
                                                    {showSuggestions && suggestions.length > 0 && (
                                                        <Box sx={{ position: 'absolute', zIndex: 1300, width: '100%', background: '#fff', boxShadow: '0 4px 8px rgba(0,0,0,0.06)', borderRadius: 1, mt: 1 }}>
                                                            {suggestions.map((s, i) => (
                                                                <Box key={i} sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { background: '#F9FAFB' } }} onClick={() => {
                                                                    const [lng, lat] = s.center || [null, null];
                                                                    if (lng != null && lat != null) {
                                                                        setCoords({ lat, lng });
                                                                        setCoordsTouched(true);
                                                                        // mimic CreateNode behaviour: update search query and hide suggestions
                                                                        setSearchQuery(s.place_name || '');
                                                                        setShowSuggestions(false);
                                                                        try { const map = mapRef.current?.getMap && mapRef.current.getMap(); if (map && typeof map.flyTo === 'function') map.flyTo({ center: [lng, lat], zoom: 15 }); } catch (err) { /* ignore */ }
                                                                    }
                                                                }}>{s.place_name}</Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 2, width: '100%', height: 240, position: 'relative' }}>
                                                {isValidMapboxToken && !mapAuthError ? (
                                                    <Map
                                                        ref={mapRef}
                                                        initialViewState={{ latitude: coords.lat, longitude: coords.lng, zoom: 12 }}
                                                        style={{ width: '100%', height: '100%' }}
                                                        mapStyle={MAPBOX_STYLE}
                                                        mapboxAccessToken={mapboxToken}
                                                        onClick={(evt: any) => {
                                                            // click to set centre (matches CreateNode)
                                                            try {
                                                                const [lng, lat] = Array.isArray(evt?.lngLat) ? evt.lngLat : [evt?.lngLat?.lng ?? evt?.lng, evt?.lngLat?.lat ?? evt?.lat];
                                                                if (lng != null && lat != null) {
                                                                    setCoords({ lat, lng });
                                                                    setCoordsTouched(true);
                                                                }
                                                            } catch (err) { /* ignore */ }
                                                        }}
                                                    >
                                                        {(circleFeature || polygonGeoJSON) && (
                                                            <Source id="station-circle" type="geojson" data={circleFeature || polygonGeoJSON}>
                                                                <Layer id="station-circle-fill" type="fill" paint={{ 'fill-color': '#2DB8A6', 'fill-opacity': 0.15 }} />
                                                                {/* KAN-203: draw a visible geofence-radius outline. Previously
                                                                    only the near-invisible 0.12-opacity fill was rendered, so the
                                                                    radius had no visible marker on the LE-station map. */}
                                                                <Layer id="station-circle-outline" type="line" paint={{ 'line-color': '#2DB8A6', 'line-width': 2, 'line-opacity': 0.9 }} />
                                                            </Source>
                                                        )}
                                                        <Marker longitude={coords.lng} latitude={coords.lat} anchor="bottom">
                                                            <Box sx={{ width: 24, height: 24, backgroundColor: '#2DB8A6', borderRadius: '50%', border: '2px solid #fff' }} />
                                                        </Marker>
                                                    </Map>
                                                ) : (
                                                    <Box sx={mapPlaceholderSx}>
                                                        <Box sx={{ p: 2 }}>{mapAuthError ?? 'Map disabled: provide a valid REACT_APP_MAPBOX_TOKEN starting with "pk.".'}</Box>
                                                    </Box>
                                                )}
                                                <Box sx={coordsBadgeSx}>
                                                    <Typography variant="caption">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={radiusContainerSx}>
                                                <Typography variant="caption">Geofence radius (km)</Typography>
                                                <Box>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <Typography variant="body2">0.05 km</Typography>
                                                        <Box sx={sliderWrapperSx}>
                                                            <Slider min={0.05} max={100} step={0.05} value={Math.min(100, Math.max(0.05, radius || 0.05))} aria-label="geofence-radius" onChange={(_e, v) => { setRadius(Array.isArray(v) ? v[0] : v); setRadiusTouched(true); }} sx={{ mt: 0 }} />
                                                        </Box>
                                                        <Typography variant="body2">100 km</Typography>
                                                    </Box>
                                                    <Box display="flex" justifyContent="flex-end" mt={0.5}>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={radius}
                                                            onChange={(e) => { setRadius(Number(e.target.value)); setRadiusTouched(true); }}
                                                            slotProps={{ htmlInput: { step: 0.05 }, input: { endAdornment: <InputAdornment position="end"><Typography variant="caption">km</Typography></InputAdornment> } }}
                                                            sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 34 }, '& input': { textAlign: 'center', px: 1 } }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                {jurisdictionMode === 'select' && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography sx={textSx as any}>District</Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <Autocomplete
                                                options={loadingDistricts ? [] : districts}
                                                loading={loadingDistricts}
                                                value={district || null}
                                                onChange={async (_e, val) => {
                                                    const selected = val ?? '';
                                                    setDistrict(selected);
                                                    setDistrictTouched(true);
                                                    // try to geocode the district name and set coords
                                                    if (selected && isValidMapboxToken) {
                                                        try {
                                                            const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
                                                            const q = encodeURIComponent(selected);
                                                            const url = `${host}/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxToken}&autocomplete=false&limit=1`;
                                                            const res = await fetch(url);
                                                            if (!res.ok) return;
                                                            const body = await res.json().catch(() => null);
                                                            const feat = Array.isArray(body?.features) && body.features[0];
                                                            if (feat) {
                                                                const [lng, lat] = feat.center || [null, null];
                                                                if (lng != null && lat != null) {
                                                                    setCoords({ lat, lng });
                                                                    try { const map = mapRef.current?.getMap && mapRef.current.getMap(); if (map && typeof map.flyTo === 'function') map.flyTo({ center: [lng, lat], zoom: 12 }); } catch (err) { /* ignore */ }
                                                                }
                                                            }
                                                        } catch (err) { /* ignore */ }
                                                    }
                                                }}
                                                size="small"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="-- Select district --"
                                                        sx={dropdownSx}
                                                        error={Boolean(errors.district)}
                                                    />
                                                )}
                                            />
                                            {errors.district && <Typography color="error" variant="caption">{errors.district}</Typography>}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                        </Paper>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, width: '883px' }}>
                            <Box>
                                {initialData && initialData.id && (
                                            <Button variant="text" sx={deactivateButtonSx as any} onClick={() => setConfirmOpen(true)}>Deactivate station</Button>
                                        )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    type="button"
                                    sx={saveButtonSx as any}
                                    onMouseDown={() => console.log('[CreateStation] Save mouseDown')}
                                    disabled={saving}
                                    onClick={async () => {
                                        if (saving) return; // guard against double-click double-submit
                                        console.log('[CreateStation] Save clicked', { initialDataId: initialData?.id, jurisdictionMode, coords, radius });
                                        // client-side validation
                                        const e: Record<string, string> = {};
                                        if (!stationName || stationName.trim().length < 2) e.name = 'Station name is required (min 2 chars)';
                                        if (!primaryName || primaryName.trim().length < 2) e.primaryName = 'Primary contact name is required';
                                        if (!primaryEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(primaryEmail)) e.primaryEmail = 'Valid email is required';
                                        if (jurisdictionMode === 'draw' && (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number')) e.coords = 'Select a location on the map';
                                        if (jurisdictionMode === 'draw' && (!radius || radius < 0.05 || radius > 100)) e.radius = 'Radius must be between 0.05 km and 100 km';
                                        if (jurisdictionMode === 'select' && !district) e.district = 'Please select a district';
                                        setErrors(e);
                                        if (Object.keys(e).length) return;

                                        setSaving(true);
                                        // build full object we would send
                                        const full = {
                                            name: stationName.trim(),
                                            district,
                                            latitude: Number(coords?.lat),
                                            longitude: Number(coords?.lng),
                                            geofence_radius_m: Math.round(radius * 1000),
                                            primary_contact: primaryName.trim(),
                                            email: primaryEmail.trim(),
                                        };
                                        try {
                                            if (initialData && initialData.id) {
                                                // compute minimal patch: only include keys that changed compared to initialData
                                                const patch: Record<string, any> = {};
                                                // Normalize initialData into the same output keys we will send to the API
                                                    const initialNormalized: Record<string, any> = {
                                                    name: (initialData as any)?.stationName ?? (initialData as any)?.name ?? '',
                                                    district: (initialData as any)?.district ?? (initialData as any)?.jurisdiction ?? '',
                                                    latitude: (initialData as any)?.latitude ?? (initialData as any)?.lat ?? (initialData as any)?.location?.lat ?? undefined,
                                                    longitude: (initialData as any)?.longitude ?? (initialData as any)?.lng ?? (initialData as any)?.lon ?? (initialData as any)?.location?.lng ?? undefined,
                                                    geofence_radius_m: (initialData as any)?.geofence_radius_m ?? (initialData as any)?.radius ?? (initialData as any)?.geofenceRadius ?? undefined,
                                                    primary_contact: (initialData as any)?.primary_contact ?? (initialData as any)?.primaryName ?? (initialData as any)?.primaryContact ?? '',
                                                    email: (initialData as any)?.email ?? (initialData as any)?.primaryEmail ?? '',
                                                };

                                                console.debug('[CreateStation] initialNormalized', initialNormalized);
                                                // compare normalized initial values to current `full` object
                                                Object.keys(full).forEach((k) => {
                                                    const curr = (full as any)[k];
                                                    const orig = initialNormalized[k];
                                                    if (k === 'latitude' || k === 'longitude') {
                                                        // only include coords when user touched the map or the station originally had coords and they changed
                                                        if (coordsTouched) {
                                                            if (typeof curr === 'number' && (typeof orig === 'undefined' || Number(orig) !== Number(curr))) patch[k] = curr;
                                                        } else if (typeof orig !== 'undefined') {
                                                            if (Number(orig) !== Number(curr)) patch[k] = curr;
                                                        }
                                                    } else if (k === 'geofence_radius_m') {
                                                        if (radiusTouched) {
                                                            if (typeof curr === 'number' && (typeof orig === 'undefined' || Number(orig) !== Number(curr))) patch[k] = curr;
                                                        } else if (typeof orig !== 'undefined') {
                                                            if (Number(orig) !== Number(curr)) patch[k] = curr;
                                                        }
                                                    } else if (k === 'district') {
                                                        // only send district when user changed it and they didn't pick coords on map
                                                        if (!coordsTouched && (orig ?? '') !== (curr ?? '')) patch[k] = curr;
                                                    } else {
                                                        if (typeof curr === 'number') {
                                                            if (typeof orig === 'undefined' || Number(orig) !== Number(curr)) patch[k] = curr;
                                                        } else {
                                                            if ((orig ?? '') !== (curr ?? '')) patch[k] = curr;
                                                        }
                                                    }
                                                });
                                                    // if our comparison somehow yielded no patch but the full & initialNormalized differ (edge cases), send full as patch
                                                    const hasPatch = Object.keys(patch).length > 0;
                                                    const anyDiff = Object.keys(full).some((k) => {
                                                        const curr = (full as any)[k];
                                                        const orig = initialNormalized[k];
                                                        if (typeof curr === 'number') return typeof orig === 'undefined' || Number(orig) !== Number(curr);
                                                        return (orig ?? '') !== (curr ?? '');
                                                    });
                                                    if (!hasPatch) {
                                                        if (!anyDiff) {
                                                            window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'No changes to save' } }));
                                                            onSave && onSave(initialData);
                                                            return;
                                                        }
                                                        // fallback: send full object when we detected a difference but patch is empty
                                                        Object.assign(patch, full);
                                                    }
                                                    console.debug('[CreateStation] Patch payload', patch);
                                                    // call PATCH endpoint with minimal fields
                                                    const resp = await editLEStation(initialData.id, patch);
                                                if (resp?.success === false) {
                                                    setFormError(resp?.message ?? 'Failed to update station');
                                                } else {
                                                    window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'Station updated' } }));
                                                    onSave && onSave(resp);
                                                }
                                            } else {
                                                // For create: only include coords if user touched the map. Include district only if provided.
                                                const createPayload: Record<string, any> = {
                                                    name: full.name,
                                                    primary_contact: full.primary_contact,
                                                    email: full.email,
                                                };
                                                if (district && district.length) createPayload.district = district;
                                                // include coords/radius only when user explicitly touched the map
                                                if (coordsTouched) {
                                                    createPayload.latitude = Number(coords?.lat);
                                                    createPayload.longitude = Number(coords?.lng);
                                                    createPayload.geofence_radius_m = Math.round(radius * 1000);
                                                }
                                                // if user provided both district and coords (touched), include everything (already handled)
                                                const resp = await createLEStation(createPayload);
                                                if (resp?.success === false) {
                                                    setFormError(resp?.message ?? 'Failed to create station');
                                                } else {
                                                    window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'Station created' } }));
                                                    onSave && onSave(resp);
                                                }
                                            }
                                        } catch (err: any) {
                                            const msg = err?.message ?? 'Failed to save station';
                                            console.error('[CreateStation] Save error', err);
                                            setFormError(msg);
                                        } finally {
                                            setSaving(false);
                                        }
                                    }}
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                                <Button sx={cancelButtonSx as any} onClick={() => onCancel && onCancel()}>Cancel</Button>
                            </Box>
                        </Box>
                    </>
                )}

                {initialData && viewMode === 'userslist' && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, width: '100%', maxWidth: '1100px', alignSelf: 'flex-start', position: 'relative' }}>
                            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '14px', lineHeight: '21px' }}>{`${stationName || initialData?.stationName || 'Station'} - Users`}</Typography>
                            <Button
                                onClick={openAddUserDialog}
                                variant="contained"
                                startIcon={<Box component="img" src={userIcon} sx={{ width: 16, height: 16 }} />}
                                sx={{
                                    width: '109.375px',
                                    height: '35.5px',
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    fontFamily: 'Inter, Arial, sans-serif',
                                    fontWeight: 500,
                                    fontStyle: 'normal',
                                    fontSize: '13px',
                                    lineHeight: '19.5px',
                                    letterSpacing: '-0.08px',
                                    textAlign: 'center',
                                    backgroundColor: '#39A9B1',
                                    color: '#FFFFFF',
                                    '&:hover': { backgroundColor: '#2e8b8f' }
                                }}
                            >
                                Add user
                            </Button>

                        </Box>
                        <Paper sx={nodeDetailsSx as any}>
                            <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
                                <DataGrid
                                    rows={stationUserRows}
                                    columns={columns}
                                    getRowId={(row: any) => row.id ?? row.user_id ?? row.email ?? `${row.name ?? 'user'}-${String(row.email ?? Math.random()).replace(/\s+/g, '')}`}
                                    columnHeaderHeight={30}
                                    rowHeight={45}
                                    pagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={(model: { page: number; pageSize: number }) => {
                                        setPaginationModel(model);
                                        setPageSize(model.pageSize);
                                    }}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                    rowCount={stationUserRowCount}
                                    loading={stationUserLoading}
                                    density="comfortable"
                                    disableRowSelectionOnClick
                                    sx={{
                                        '& .MuiDataGrid-root': { position: 'relative' },
                                        '& .MuiDataGrid-columnHeaders': {
                                            backgroundColor: '#F9FAFB !important',
                                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                                            minHeight: '40px !important',
                                            maxHeight: '40px !important',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                        },
                                        '& .MuiDataGrid-virtualScroller': {
                                            maxHeight: 'calc(100vh - 260px)',
                                            overflow: 'auto',
                                            paddingBottom: '56px',
                                        },
                                        '& .MuiDataGrid-footerContainer': {
                                            position: 'absolute',
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            zIndex: 4,
                                            background: '#FFFFFF',
                                            borderTop: '1px solid rgba(0,0,0,0.06)'
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            backgroundColor: '#F9FAFB !important',
                                            minHeight: '40px !important',
                                            maxHeight: '40px !important',
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRight: 'none',
                                        },
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontFamily: 'Inter, Arial, sans-serif',
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            lineHeight: '16.5px',
                                            letterSpacing: '0.06px',
                                        },
                                        '& .MuiDataGrid-columnSeparator': { display: 'none' },
                                        '& .MuiDataGrid-cell': { borderRight: 'none' },
                                        '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                    }}
                                />
                            </div>
                        </Paper>
                    </>
                )}
            </Box>
            {/* Add User Dialog */}
            <Dialog
                open={openAddDialog}
                onClose={closeAddUserDialog}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '560px',
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ width: '560px', height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '24px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '16px', lineHeight: '24px', letterSpacing: '-0.31px' }}>Add user</Typography>
                    <IconButton size="small" onClick={closeAddUserDialog} sx={{ width: '16px', height: '16px' }}>
                        <CloseIcon sx={{ width: '16px', height: '16px' }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ px: '24px', pt: '16px' }}>
                    {addUserApiError && (
                        <Box sx={{ position: 'relative', mb: 2, width: '100%', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, py: 1.5, gap: 1 }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
                            </Box>
                            <Typography sx={{ flex: 1, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', color: '#374151' }}>{addUserApiError}</Typography>
                            <Box component="span" onClick={() => setAddUserApiError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1 }}>×</Box>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151', mb: '6px' }}>Name *</Typography>
                            <TextField
                                placeholder="Name" value={newName}
                                onChange={(e) => { setNewName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }}
                                size="small" fullWidth error={!!errors.name} helperText={errors.name}
                                sx={{ ...formInputSx as any }}
                            />
                        </Box>

                        <Box>
                            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151', mb: '6px' }}>Email *</Typography>
                            <TextField
                                placeholder="email@domain.com" value={newEmail}
                                onChange={(e) => { setNewEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: '' })); }}
                                size="small" fullWidth error={!!errors.email} helperText={errors.email}
                                sx={{ ...formInputSx as any }}
                            />
                        </Box>

                        <Box>
                            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151', mb: '6px' }}>Phone (optional)</Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <FormControl size="small" sx={{ width: '180px' }}>
                                    <select value={newCountry} onChange={(e) => setNewCountry(e.target.value)} style={{ height: '41.5px', borderRadius: '10px', paddingLeft: '12px', border: '1px solid #E5E7EB', background: 'white' }}>
                                        {ctd.allCountries.map((c: any) => {
                                            const rawName = c.name ?? c[0] ?? String(c);
                                            const cleanName = String(rawName).replace(/\s*\(.*\)\s*$/, '');
                                            return (
                                                <option key={c.iso2 ?? cleanName} value={c.dialCode ? `+${c.dialCode}` : ''}>{`${cleanName} (+${c.dialCode})`}</option>
                                            );
                                        })}
                                    </select>
                                </FormControl>
                                <TextField fullWidth size="small" placeholder="7700 900000" value={newMobile} onChange={(e) => setNewMobile(e.target.value)} sx={{ ...formInputSx as any }} />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: '24px', pb: '16px', pt: 0, gap: 2 }}>
                    <Button
                        onClick={closeAddUserDialog}
                        variant="outlined"
                        sx={{
                            width: '76.4921875px',
                            height: '37.5px',
                            borderRadius: '10px',
                            border: '1px solid #E5E7EB',
                            backgroundColor: 'transparent',
                            color: '#374151',
                            textTransform: 'none',
                            fontFamily: 'Inter, Arial, sans-serif',
                            fontWeight: 500,
                            fontSize: '13px',
                            lineHeight: '19.5px',
                            letterSpacing: '-0.08px',
                            textAlign: 'center'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={createUser}
                        variant="contained"
                        sx={{
                            width: '103.953125px',
                            height: '37.5px',
                            borderRadius: '10px',
                            gap: '8px',
                            pt: '8px',
                            pr: '16px',
                            pb: '8px',
                            pl: '16px',
                            backgroundColor: '#39A9B1',
                            color: '#FFFFFF',
                            textTransform: 'none',
                            fontFamily: 'Inter, Arial, sans-serif',
                            fontWeight: 500,
                            fontSize: '13px',
                            lineHeight: '19.5px',
                            letterSpacing: '-0.08px',
                            textAlign: 'center',
                            '&:hover': { backgroundColor: '#2e8b8f' }
                        }}
                    >
                        Create user
                    </Button>
                </DialogActions>
            </Dialog>
            {/* User deactivate confirmation modal */}
            <Modal open={userConfirmOpen} onClose={() => { setUserConfirmOpen(false); setUserConfirmTarget(null); }}>
                <Box sx={modalContainerSx}>
                    <Box sx={modalHeaderSx}>
                        <Typography sx={modalTitleSx}>{userConfirmTarget?.status === 'Inactive' ? 'Activate user?' : 'Deactivate user?'}</Typography>
                        <IconButton size="small" onClick={() => { setUserConfirmOpen(false); setUserConfirmTarget(null); }} sx={modalCloseIconSx}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></IconButton>
                    </Box>
                    <Box sx={modalContentSx}>
                        <Box sx={modalWarningRowSx}>
                            <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2', p: 0 }}>
                                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px' }} />
                            </Box>
                            <Box><Typography sx={modalBodyTextSx}>{userConfirmTarget?.status === 'Inactive' ? 'Activating this user will restore their access to this station.' : 'Deactivating this user will revoke their access to this station.'}</Typography></Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" gap={2}><Button sx={modalCancelButtonSx} onClick={() => { setUserConfirmOpen(false); setUserConfirmTarget(null); }}>Cancel</Button><Button sx={modalDeactivateButtonSx} onClick={confirmToggleUser}>{userConfirmTarget?.status === 'Inactive' ? 'Activate' : 'Deactivate'}</Button></Box>
                    </Box>
                </Box>
            </Modal>
            {/* Deactivate confirmation modal */}
            <Modal open={confirmOpen} onClose={closeConfirm}>
                <Box sx={modalContainerSx}>
                    <Box sx={modalHeaderSx}>
                        <Typography sx={modalTitleSx}>Deactivate station?</Typography>
                        <IconButton size="small" onClick={closeConfirm} sx={modalCloseIconSx}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></IconButton>
                    </Box>
                    <Box sx={modalContentSx}>
                        <Box sx={modalWarningRowSx}>
                            <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2', p: 0 }}>
                                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px' }} />
                            </Box>
                            <Box><Typography sx={modalBodyTextSx}>Deactivated stations cannot receive alerts. This action cannot be undone.</Typography></Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" gap={2}><Button sx={modalCancelButtonSx} onClick={closeConfirm}>Cancel</Button><Button sx={modalDeactivateButtonSx} onClick={() => { confirmDeactivate(); }}>Deactivate</Button></Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default CreateStation;
